
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import {
  Mail,
  MapPin,
  Clock,
  MessageCircle,
  Globe,
  Pencil,
  Save,
  X,
  Info,
} from "lucide-react"
import Cropper, { Area } from "react-easy-crop"
import { Button } from "components/ui/button"
import { Badge } from "components/ui/badge"
import { Card, CardContent, CardTitle } from "components/ui/card"
import { Avatar, AvatarFallback, AvatarImage } from "components/ui/avatar"
import { Input } from "components/ui/input"
import { Textarea } from "components/ui/textarea"
import { Label } from "components/ui/label"
import { Slider } from "components/ui/slider"
import { Switch } from "components/ui/switch"
import { ToggleGroup, ToggleGroupItem } from "components/ui/toggle-group"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "components/ui/select"
import { Dialog, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "components/ui/dialog"
import { PageLoadingState } from "components/layout/PageLoadingState"
import { ResponsiveDialogContent } from "components/ui/responsive-dialog-content"
import { useParams } from "react-router-dom"
import { validatePasswordStrength } from "utils/passwordValidation"
import { PasswordInput } from "components/ui/password-input"
import { Alert, AlertDescription, AlertTitle } from "components/ui/alert"
import { useEventTracking } from "hooks/useEventTracking"
import { useAuth } from "contexts/AuthContext"
import { getPrimaryCohort } from "utils/cohortSelection"
import { waitForImage } from "utils/waitForImage"
import { user as userApi } from "services/api"
import { images as imageApi } from "services/api/mediaClient"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "components/ui/tooltip"
import { MobileNotificationSettings } from "mobile/MobileNotificationSettings"

type Org = { id: string; name: string; org_id: string } | null

type Cohort = {
  id: string
  name: string
  is_active: boolean
  role?: string
  start_date?: string
  end_date?: string
}

type ProfileDetails = {
  id?: string
  location?: string
  headline?: string
  tags?: string[]
  bio?: string
  fun_facts?: string[]
  working_on?: string[]
  dislikes?: string[]
  boundaries?: string[]
  languages?: string[]
  response_time_blurb?: string
  preferred_contact_blurb?: string
  avatar_url?: string | null
}

type NotificationSettings = {
  meeting_notifications_enabled?: boolean
  meeting_notification_channel?: "email" | "sms" | "email_sms"
  meeting_notification_lead_minutes?: number
  in_app_meeting_reminders_enabled?: boolean
  mentorship_recap_notifications_enabled?: boolean
  meeting_notification_fixed_options?: number[]
  meeting_notification_custom_min?: number
  meeting_notification_custom_max?: number
}

interface UserProfileData {
  id: number
  username: string
  email: string
  phone_number?: string | null
  sms_opted_out_at?: string | null
  first_name?: string
  last_name?: string
  timezone?: string
  timezone_offset?: number
  organization: Org
  has_usable_password?: boolean
  profile_color?: string | null
  cohorts?: Cohort[]
  profile?: ProfileDetails
  settings?: NotificationSettings
  is_self?: boolean
}

type AlertState =
  | {
      variant: "default" | "destructive" | "success"
      title: string
      description?: string
    }
  | null

type BasicProfileField = "timezone" | "tags"
type BasicProfileFieldErrors = Partial<Record<BasicProfileField, string>>

const DEFAULT_FIXED_LEAD_OPTIONS = [5, 10, 15, 30, 60, 120]

const isValidTimeZone = (value: string) => {
  try {
    Intl.DateTimeFormat(undefined, { timeZone: value }).format()
    return true
  } catch {
    return false
  }
}

const getApiFieldError = (error: any, field: BasicProfileField) => {
  const value = error?.response?.data?.[field]
  if (Array.isArray(value)) return value.filter(Boolean).join(" ")
  return typeof value === "string" ? value : undefined
}

export default function MentorProfilePage() {
  const { user: currentUser, updateUser } = useAuth()
  const { userId } = useParams()

  const sanitizeList = (list: string[] | undefined | null) =>
    Array.isArray(list) ? list.map((item) => (item ?? "").trim()).filter(Boolean) : []

  const [profile, setProfile] = useState<UserProfileData | null>(null)
  const [alert, setAlert] = useState<AlertState>(null)
  const [basicFieldErrors, setBasicFieldErrors] = useState<BasicProfileFieldErrors>({})
  const [notFound, setNotFound] = useState(false)
  const { track } = useEventTracking()
  const [loadingProfile, setLoadingProfile] = useState(true)
  const [savingBasic, setSavingBasic] = useState(false)
  const [savingFunFacts, setSavingFunFacts] = useState(false)
  const [savingWorkingOn, setSavingWorkingOn] = useState(false)
  const [savingBoundaries, setSavingBoundaries] = useState(false)
  const [savingContact, setSavingContact] = useState(false)
  const [savingNotifications, setSavingNotifications] = useState(false)

  const [name, setName] = useState("")
  const [location, setLocation] = useState("")
  const [timezone, setTimezone] = useState("UTC")
  const [headline, setHeadline] = useState("")
  const [tags, setTags] = useState<string[]>([])
  const [bio, setBio] = useState("")
  const [email, setEmail] = useState("")
  const [phoneNumber, setPhoneNumber] = useState("")
  const [responseTime, setResponseTime] = useState("")
  const [contactMethod, setContactMethod] = useState("")
  const [languages, setLanguages] = useState<string[]>([])
  const [funFacts, setFunFacts] = useState<string[]>([])
  const [workingOn, setWorkingOn] = useState<string[]>([])
  const [dontLove, setDontLove] = useState<string[]>([])
  const [boundaries, setBoundaries] = useState<string[]>([])

  const [editingBasicInfo, setEditingBasicInfo] = useState(false)
  const [editingFunFacts, setEditingFunFacts] = useState(false)
  const [editingWorkingOn, setEditingWorkingOn] = useState(false)
  const [editingBoundaries, setEditingBoundaries] = useState(false)
  const [editingContact, setEditingContact] = useState(false)
  const [editingNotifications, setEditingNotifications] = useState(false)

  const [meetingNotificationsEnabled, setMeetingNotificationsEnabled] = useState(true)
  const [meetingNotificationChannel, setMeetingNotificationChannel] = useState<"email" | "sms" | "email_sms">("email")
  const [meetingNotificationLeadMinutes, setMeetingNotificationLeadMinutes] = useState<number>(30)
  const [inAppMeetingRemindersEnabled, setInAppMeetingRemindersEnabled] = useState(true)
  const [mentorshipRecapNotificationsEnabled, setMentorshipRecapNotificationsEnabled] = useState(true)

  const [currentPassword, setCurrentPassword] = useState("")
  const [newPassword, setNewPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const [changingPassword, setChangingPassword] = useState(false)
  const [uploadingAvatar, setUploadingAvatar] = useState(false)
  const avatarInputRef = useRef<HTMLInputElement | null>(null)
  const isMounted = useRef(true)
  useEffect(() => {
    isMounted.current = true
    return () => { isMounted.current = false }
  }, [])
  const [avatarKey, setAvatarKey] = useState<string | null>(null)
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null)
  const [showCropper, setShowCropper] = useState(false)
  const [cropImageSrc, setCropImageSrc] = useState<string | null>(null)
  const [pendingAvatarFile, setPendingAvatarFile] = useState<File | null>(null)
  const [crop, setCrop] = useState({ x: 0, y: 0 })
  const [zoom, setZoom] = useState(1)
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null)
  const [cropping, setCropping] = useState(false)

  const requiresCurrentPassword = profile?.has_usable_password !== false

  const { message: passwordValidationMessage } = useMemo(() => validatePasswordStrength(newPassword), [newPassword])

  const restoreFieldsFromProfile = useCallback(() => {
    if (!profile) return
    const details = profile.profile ?? {}
    const settings = profile.settings ?? {}
    const fixedOptions = settings.meeting_notification_fixed_options?.length
      ? settings.meeting_notification_fixed_options
      : DEFAULT_FIXED_LEAD_OPTIONS
    const fallbackLead = settings.meeting_notification_lead_minutes ?? 30
    const effectiveLead = fixedOptions.includes(fallbackLead) ? fallbackLead : fixedOptions[0]
    setName(`${profile.first_name ?? ""} ${profile.last_name ?? ""}`.trim() || profile.username || profile.email || "")
    setLocation((details.location ?? "").trim())
    setTimezone(profile.timezone ?? "UTC")
    setHeadline((details.headline ?? "").trim())
    setTags(sanitizeList(details.tags))
    setBio((details.bio ?? "").trim())
    setEmail((profile.email ?? "").trim())
    setPhoneNumber((profile.phone_number ?? "").trim())
    setResponseTime((details.response_time_blurb ?? "").trim())
    setContactMethod((details.preferred_contact_blurb ?? "").trim())
    setLanguages(sanitizeList(details.languages))
    setFunFacts(sanitizeList(details.fun_facts))
    setWorkingOn(sanitizeList(details.working_on))
    setDontLove(sanitizeList(details.dislikes))
    setBoundaries(sanitizeList(details.boundaries))
    setMeetingNotificationsEnabled(settings.meeting_notifications_enabled ?? true)
    setMeetingNotificationChannel(settings.meeting_notification_channel ?? "email")
    setMeetingNotificationLeadMinutes(effectiveLead)
    setInAppMeetingRemindersEnabled(settings.in_app_meeting_reminders_enabled ?? true)
    setMentorshipRecapNotificationsEnabled(settings.mentorship_recap_notifications_enabled ?? true)
  }, [profile])

  const hydrateProfile = useCallback((data: UserProfileData) => {
    const details = data.profile ?? {}
    const settings = data.settings ?? {}
    const fixedOptions = settings.meeting_notification_fixed_options?.length
      ? settings.meeting_notification_fixed_options
      : DEFAULT_FIXED_LEAD_OPTIONS
    const fallbackLead = settings.meeting_notification_lead_minutes ?? 30
    const effectiveLead = fixedOptions.includes(fallbackLead) ? fallbackLead : fixedOptions[0]
    const fullName = `${data.first_name ?? ""} ${data.last_name ?? ""}`.trim() || data.username || data.email || ""

    setProfile(data)
    setName(fullName)
    setLocation((details.location ?? "").trim())
    setTimezone(data.timezone ?? "UTC")
    setHeadline((details.headline ?? "").trim())
    setTags(sanitizeList(details.tags))
    setBio((details.bio ?? "").trim())
    setEmail((data.email ?? "").trim())
    setPhoneNumber((data.phone_number ?? "").trim())
    setResponseTime((details.response_time_blurb ?? "").trim())
    setContactMethod((details.preferred_contact_blurb ?? "").trim())
    setLanguages(sanitizeList(details.languages))
    setFunFacts(sanitizeList(details.fun_facts))
    setWorkingOn(sanitizeList(details.working_on))
    setDontLove(sanitizeList(details.dislikes))
    setBoundaries(sanitizeList(details.boundaries))
    setMeetingNotificationsEnabled(settings.meeting_notifications_enabled ?? true)
    setMeetingNotificationChannel(settings.meeting_notification_channel ?? "email")
    setMeetingNotificationLeadMinutes(effectiveLead)
    setInAppMeetingRemindersEnabled(settings.in_app_meeting_reminders_enabled ?? true)
    setMentorshipRecapNotificationsEnabled(settings.mentorship_recap_notifications_enabled ?? true)
    const avatarValue = details.avatar_url ?? null
    setAvatarKey(avatarValue)
    if (avatarValue && avatarValue.startsWith("http")) {
      setAvatarUrl(avatarValue)
    } else {
      setAvatarUrl(null)
    }
  }, [])

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setLoadingProfile(true)
        setNotFound(false)
        const viewerId = currentUser?.id ? String(currentUser.id) : null
        const shouldLoadOtherUser = userId && userId !== viewerId
        const data = shouldLoadOtherUser ? await userApi.getById(userId as string) : await userApi.getMe()
        hydrateProfile(data)
      } catch (e: any) {
        if (e?.response?.status === 404 || e?.response?.status === 403) {
          setNotFound(true)
          setAlert(null)
          return
        }
        setAlert({
          variant: "destructive",
          title: "Failed to load profile",
          description: e?.response?.data?.detail || e?.message || "Please try again later.",
        })
      } finally {
        setLoadingProfile(false)
      }
    }
    fetchProfile()
  }, [hydrateProfile, userId, currentUser?.id])

  useEffect(() => {
    let current = true
    const resolveAvatarUrl = async () => {
      if (!avatarKey) {
        setAvatarUrl(null)
        return
      }
      if (avatarKey.startsWith("http")) {
        setAvatarUrl(avatarKey)
        return
      }
      try {
        const url = await imageApi.getSignedUrl(avatarKey)
        if (current) setAvatarUrl(url)
      } catch (e) {
        if (current) setAvatarUrl(null)
      }
    }
    void resolveAvatarUrl()
    return () => {
      current = false
    }
  }, [avatarKey])

  const canEdit = useMemo(() => {
    if (!profile || !currentUser) return false
    if (userId && userId !== String(currentUser.id)) return false
    return profile.id === currentUser.id
  }, [currentUser, profile, userId])

  const displayName = useMemo(() => {
    if (name.trim()) return name
    if (profile?.username) return profile.username
    return profile?.email ?? ""
  }, [name, profile?.username, profile?.email])

  const avatarInitials = useMemo(() => {
    const parts = displayName.trim().split(/\s+/)
    if (parts.length === 0) return "U"
    const first = parts[0]?.[0] ?? ""
    const second = parts[1]?.[0] ?? ""
    return `${first}${second}`.trim().toUpperCase() || (displayName[0]?.toUpperCase() ?? "U")
  }, [displayName])

  const activeCohortName = useMemo(() => {
    return getPrimaryCohort(profile?.cohorts)?.name ?? null
  }, [profile?.cohorts])

  const updateArrayItem = (array: string[], index: number, value: string, setter: (arr: string[]) => void) => {
    const newArray = [...array]
    newArray[index] = value
    setter(newArray)
  }

  const addArrayItem = (array: string[], setter: (arr: string[]) => void, max?: number) => {
    if (typeof max === "number" && array.length >= max) return
    setter([...array, ""])
  }

  const removeArrayItem = (array: string[], index: number, setter: (arr: string[]) => void) => {
    setter(array.filter((_, i) => i !== index))
  }

  const handleChangePassword = async () => {
    setAlert(null)

    if (requiresCurrentPassword && !currentPassword) {
      setAlert({
        variant: "destructive",
        title: "Current password required",
        description: "Enter your current password before setting a new one.",
      })
      return
    }

    if (!newPassword || !confirmPassword) {
      setAlert({
        variant: "destructive",
        title: "Missing fields",
        description: "Please provide and confirm your new password.",
      })
      return
    }
    if (newPassword !== confirmPassword) {
      setAlert({
        variant: "destructive",
        title: "Password mismatch",
        description: "New password and confirmation do not match.",
      })
      return
    }
    if (passwordValidationMessage) {
      setAlert({
        variant: "destructive",
        title: "Invalid password",
        description: passwordValidationMessage,
      })
      return
    }
    setChangingPassword(true)
    let requiresReauthentication = false
    try {
      const result = await userApi.changePassword({
        currentPassword: requiresCurrentPassword ? currentPassword : undefined,
        newPassword,
      })
      requiresReauthentication = result?.reauthentication_required === true
      if (requiresReauthentication || !isMounted.current) return
      setAlert({
        variant: "success",
        title: "Password updated",
      })
      setCurrentPassword("")
      setNewPassword("")
      setConfirmPassword("")
      setProfile((prev) => (prev ? { ...prev, has_usable_password: true } : prev))
      track("password_changed")
    } catch (e: any) {
      if (!isMounted.current) return
      const msg = e?.response?.data?.error || e?.message || "Failed to update password"
      setAlert({
        variant: "destructive",
        title: "Error updating password",
        description: msg,
      })
    } finally {
      if (isMounted.current && !requiresReauthentication) setChangingPassword(false)
    }
  }

  const handleSaveBasicInfo = async () => {
    if (!profile) return

    setAlert(null)
    const normalizedTimezone = timezone.trim()
    const normalizedTags = sanitizeList(tags)
    const validationErrors: BasicProfileFieldErrors = {}
    if (!normalizedTimezone || !isValidTimeZone(normalizedTimezone)) {
      validationErrors.timezone = "Enter a valid IANA timezone, such as America/Chicago."
    }
    if (normalizedTags.length > 3) {
      validationErrors.tags = "Use no more than 3 tags."
    }
    if (Object.keys(validationErrors).length > 0) {
      setBasicFieldErrors(validationErrors)
      return
    }

    setBasicFieldErrors({})
    setSavingBasic(true)
    try {
      const updated = await userApi.updateProfile({
        timezone: normalizedTimezone,
        location,
        headline,
        tags: normalizedTags,
        bio,
      })
      hydrateProfile(updated)
      updateUser({ timezone: updated.timezone ?? normalizedTimezone })
      setEditingBasicInfo(false)
    } catch (e: any) {
      const fieldErrors: BasicProfileFieldErrors = {
        timezone: getApiFieldError(e, "timezone"),
        tags: getApiFieldError(e, "tags"),
      }
      const definedFieldErrors = Object.fromEntries(
        Object.entries(fieldErrors).filter(([, message]) => Boolean(message)),
      ) as BasicProfileFieldErrors
      setBasicFieldErrors(definedFieldErrors)
      restoreFieldsFromProfile()
      if (Object.keys(definedFieldErrors).length === 0) {
        setAlert({
          variant: "destructive",
          title: "Failed to save profile",
          description: e?.response?.data?.detail || e?.message || "Please try again.",
        })
      }
    } finally {
      setSavingBasic(false)
    }
  }

  const handleSaveFunFacts = async () => {
    setSavingFunFacts(true)
    try {
      const updated = await userApi.updateProfile({ fun_facts: sanitizeList(funFacts) })
      hydrateProfile(updated)
      setEditingFunFacts(false)
    } catch (e: any) {
      setAlert({
        variant: "destructive",
        title: "Failed to save fun facts",
        description: e?.response?.data?.detail || e?.message || "Please try again.",
      })
    } finally {
      setSavingFunFacts(false)
    }
  }

  const handleSaveWorkingOn = async () => {
    setSavingWorkingOn(true)
    try {
      const updated = await userApi.updateProfile({ working_on: sanitizeList(workingOn) })
      hydrateProfile(updated)
      setEditingWorkingOn(false)
    } catch (e: any) {
      setAlert({
        variant: "destructive",
        title: "Failed to save updates",
        description: e?.response?.data?.detail || e?.message || "Please try again.",
      })
    } finally {
      setSavingWorkingOn(false)
    }
  }

  const handleSaveBoundaries = async () => {
    setSavingBoundaries(true)
    try {
      const updated = await userApi.updateProfile({
        dislikes: sanitizeList(dontLove),
        boundaries: sanitizeList(boundaries),
      })
      hydrateProfile(updated)
      setEditingBoundaries(false)
    } catch (e: any) {
      setAlert({
        variant: "destructive",
        title: "Failed to save boundaries",
        description: e?.response?.data?.detail || e?.message || "Please try again.",
      })
    } finally {
      setSavingBoundaries(false)
    }
  }

  const handleSaveContact = async () => {
    setSavingContact(true)
    try {
      const updated = await userApi.updateProfile({
        phone_number: phoneNumber,
        response_time_blurb: responseTime,
        preferred_contact_blurb: contactMethod,
        languages: sanitizeList(languages),
      })
      hydrateProfile(updated)
      setEditingContact(false)
    } catch (e: any) {
      setAlert({
        variant: "destructive",
        title: "Failed to save contact info",
        description: e?.response?.data?.detail || e?.message || "Please try again.",
      })
    } finally {
      setSavingContact(false)
    }
  }

  const validateMeetingLeadMinutes = () => {
    const fixedOptions =
      profile?.settings?.meeting_notification_fixed_options?.length
        ? profile.settings.meeting_notification_fixed_options
        : DEFAULT_FIXED_LEAD_OPTIONS
    if (!fixedOptions.includes(meetingNotificationLeadMinutes)) {
      return null
    }
    return meetingNotificationLeadMinutes
  }

  const handleSaveNotifications = async () => {
    const validLeadMinutes = validateMeetingLeadMinutes()
    if (validLeadMinutes === null) {
      setAlert({
        variant: "destructive",
        title: "Invalid reminder timing",
        description: "Please choose one of the available reminder timing options.",
      })
      return
    }
    setSavingNotifications(true)
    try {
      const updated = await userApi.updateProfile({
        settings: {
          meeting_notifications_enabled: meetingNotificationsEnabled,
          meeting_notification_channel: meetingNotificationChannel,
          meeting_notification_lead_minutes: validLeadMinutes,
          in_app_meeting_reminders_enabled: inAppMeetingRemindersEnabled,
          mentorship_recap_notifications_enabled: mentorshipRecapNotificationsEnabled,
        },
      })
      hydrateProfile(updated)
      setEditingNotifications(false)
    } catch (e: any) {
      setAlert({
        variant: "destructive",
        title: "Failed to save notification settings",
        description: e?.response?.data?.detail || e?.message || "Please try again.",
      })
    } finally {
      setSavingNotifications(false)
    }
  }

  const createImage = (url: string) =>
    new Promise<HTMLImageElement>((resolve, reject) => {
      const image = new Image()
      image.addEventListener("load", () => resolve(image))
      image.addEventListener("error", (error) => reject(error))
      image.setAttribute("crossOrigin", "anonymous")
      image.src = url
    })

  const getCroppedBlob = async (imageSrc: string, pixelCrop: Area, mimeType: string) => {
    const image = await createImage(imageSrc)
    const canvas = document.createElement("canvas")
    const size = Math.min(800, Math.max(pixelCrop.width, pixelCrop.height))
    canvas.width = size
    canvas.height = size
    const ctx = canvas.getContext("2d")
    if (!ctx) {
      throw new Error("Could not prepare crop canvas")
    }
    ctx.drawImage(image, pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height, 0, 0, size, size)
    return await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (blob) => {
          if (blob) resolve(blob)
          else reject(new Error("Failed to crop image"))
        },
        mimeType || "image/jpeg",
        0.9,
      )
    })
  }

  const onCropComplete = useCallback((_: Area, croppedPixels: Area) => {
    setCroppedAreaPixels(croppedPixels)
  }, [])

  const resetCropperState = () => {
    setCrop({ x: 0, y: 0 })
    setZoom(1)
    setCroppedAreaPixels(null)
    setCropImageSrc(null)
    setPendingAvatarFile(null)
    setCropping(false)
  }

  const prepareAvatarCrop = (file: File) => {
    const reader = new FileReader()
    reader.onloadend = () => {
      setCropImageSrc(reader.result as string)
      setPendingAvatarFile(file)
      setShowCropper(true)
      setCrop({ x: 0, y: 0 })
      setZoom(1)
    }
    reader.readAsDataURL(file)
  }

  const handleConfirmCrop = async () => {
    if (!cropImageSrc || !croppedAreaPixels || !pendingAvatarFile) {
      setShowCropper(false)
      resetCropperState()
      return
    }
    setCropping(true)
    try {
      const mimeType = pendingAvatarFile.type || "image/jpeg"
      const blob = await getCroppedBlob(cropImageSrc, croppedAreaPixels, mimeType)
      const croppedFile = new File([blob], pendingAvatarFile.name || "avatar.jpg", { type: mimeType })
      await handleAvatarUpload(croppedFile)
      setShowCropper(false)
      resetCropperState()
    } catch (e: any) {
      setAlert({
        variant: "destructive",
        title: "Failed to crop photo",
        description: e?.message || "Please try again.",
      })
    } finally {
      setCropping(false)
    }
  }

  const handleCancelCrop = () => {
    setShowCropper(false)
    resetCropperState()
  }

  const handleAvatarUpload = async (file: File) => {
    setUploadingAvatar(true)
    setAlert(null)
    try {
      const key = await imageApi.upload(file, { visibility: "public", folder: "avatars" })
      const updated = await userApi.updateProfile({ avatar_key: key })
      const newAvatarUrl = updated?.profile?.avatar_url ?? null
      let resolvedUrl: string | null = null
      try {
        const url = newAvatarUrl && newAvatarUrl.startsWith("http")
          ? newAvatarUrl
          : await imageApi.getSignedUrl(key)
        // Load the new photo before showing it, so the avatar swaps once instead of
        // dropping to initials and then switching between URLs.
        await waitForImage(url)
        resolvedUrl = url
      } catch {
        // The photo is already saved; the avatar effect resolves it from the profile instead.
      }
      hydrateProfile(updated)
      if (resolvedUrl) {
        // Store the resolved (possibly signed) URL as the key so the avatar effect does not
        // fetch a second URL; the next profile load replaces it with the server's value.
        setAvatarKey(resolvedUrl)
        setAvatarUrl(resolvedUrl)
        updateUser({ avatar_url: resolvedUrl })
      }
    } catch (e: any) {
      setAlert({
        variant: "destructive",
        title: "Failed to upload photo",
        description: e?.response?.data?.error || e?.message || "Please try again.",
      })
    } finally {
      setUploadingAvatar(false)
    }
  }

  if (loadingProfile) {
    return <PageLoadingState />
  }

  if (notFound) {
    return (
      <div className="py-6 sm:py-10">
        <div className="rounded-2xl border border-gray-200 bg-white shadow-sm p-8 text-center space-y-2">
          <h1 className="text-2xl font-semibold text-gray-900">User not found</h1>
          <p className="text-sm text-gray-600">That profile doesn&apos;t exist or you don&apos;t have access to view it.</p>
        </div>
      </div>
    )
  }

  return (
    <div>
      <Dialog
        open={showCropper}
        onOpenChange={(open) => {
          if (!open) {
            handleCancelCrop()
          } else {
            setShowCropper(true)
          }
        }}
      >
        <ResponsiveDialogContent size="lg" className="max-h-[90vh] sm:p-8">
          <DialogHeader>
            <DialogTitle>Adjust your profile photo</DialogTitle>
            <DialogDescription>Crop a square so it fits the circular avatar without stretching.</DialogDescription>
          </DialogHeader>
          <div className="relative h-[280px] overflow-hidden rounded-2xl bg-muted sm:h-[360px]">
            {cropImageSrc && (
              <Cropper
                image={cropImageSrc}
                crop={crop}
                zoom={zoom}
                aspect={1}
                cropShape="round"
                showGrid={false}
                onCropChange={setCrop}
                onZoomChange={(value) => setZoom(value)}
                onCropComplete={onCropComplete}
              />
            )}
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm text-muted-foreground">
              <span>Zoom</span>
              <span>{zoom.toFixed(1)}x</span>
            </div>
            <Slider value={zoom} onChange={(value) => setZoom(value)} min={1} max={3} step={0.1} />
          </div>
          <DialogFooter>
            <Button variant="ghost" size="auto" onClick={handleCancelCrop} disabled={cropping || uploadingAvatar}>
              Cancel
            </Button>
            <Button size="auto" onClick={handleConfirmCrop} disabled={cropping || uploadingAvatar}>
              {cropping || uploadingAvatar ? "Saving..." : "Save crop"}
            </Button>
          </DialogFooter>
        </ResponsiveDialogContent>
      </Dialog>

      <div className="relative py-6 sm:py-10">
        {alert && (
          <Alert variant={alert.variant === "destructive" ? "destructive" : "default"} className="mb-4">
            <AlertTitle>{alert.title}</AlertTitle>
            {alert.description && <AlertDescription>{alert.description}</AlertDescription>}
          </Alert>
        )}

        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between mb-8 gap-4">
          <div className="flex-1">
            <h1 className="mb-2 text-2xl font-medium text-gray-900 text-balance sm:text-4xl">{displayName}</h1>
            <p className="text-base text-gray-700 italic leading-relaxed">
              {(editingBasicInfo ? profile?.profile?.headline : headline) || "Add a headline"}
            </p>
          </div>
          {activeCohortName && (
            <Badge variant="outline" className="w-fit px-4 py-2 text-sm bg-white/80 border-gray-300">
              Cohort: {activeCohortName}
            </Badge>
          )}
        </div>

        <Card maxWidth="none" className="mb-6">
          <div className="relative">
            {canEdit && (
              <Button
                variant="ghost"
                size="auto"
                onClick={() => {
                  if (editingBasicInfo) {
                    restoreFieldsFromProfile()
                    setBasicFieldErrors({})
                    setEditingBasicInfo(false)
                  } else {
                    setBasicFieldErrors({})
                    setEditingBasicInfo(true)
                  }
                }}
                aria-label={editingBasicInfo ? "Cancel editing basic information" : "Edit basic information"}
                className="absolute top-6 right-6 text-gray-600 hover:text-gray-900 rounded-xl z-10"
              >
                {editingBasicInfo ? <X className="h-4 w-4" /> : <Pencil className="h-4 w-4" />}
              </Button>
            )}
            <CardContent variant="custom" className="sm:p-6 flex flex-col gap-6 items-start">
              {editingBasicInfo ? (
                <form
                  className="space-y-6 w-full"
                  autoComplete="off"
                  onSubmit={(e) => e.preventDefault()}
                >
                <div className="flex flex-col md:flex-row gap-6">
                    <div className="flex flex-col items-center gap-3">
                      <Avatar className="h-24 w-24 border-4 border-white shadow-lg">
                        <AvatarImage src={avatarUrl ?? undefined} className="object-cover" />
                        <AvatarFallback className="bg-mentor-accent text-white text-2xl">{avatarInitials}</AvatarFallback>
                      </Avatar>
                      <input
                        ref={avatarInputRef}
                        type="file"
                        accept="image/*"
                        aria-label="Choose a profile photo"
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files?.[0]
                          if (file) {
                            prepareAvatarCrop(file)
                          }
                          if (e.target) {
                            e.target.value = ""
                          }
                        }}
                      />
                      <Button
                        variant="secondary"
                        size="auto"
                        className="rounded-xl"
                        disabled={uploadingAvatar}
                        type="button"
                        onClick={() => avatarInputRef.current?.click()}
                      >
                        {uploadingAvatar ? "Uploading..." : "Upload Photo"}
                      </Button>
                    </div>

                  <div className="flex-1 space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="name">Name</Label>
                        <Input
                          id="name"
                          name="profile-name"
                          type="text"
                          value={name}
                          readOnly
                          disabled
                          autoComplete="off"
                          inputMode="text"
                          className="rounded-xl text-base leading-none"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="location">Location</Label>
                        <Input
                          id="location"
                          name="profile-location"
                          type="text"
                          value={location}
                          onChange={(e) => setLocation(e.target.value)}
                          autoComplete="off"
                          inputMode="text"
                          className="rounded-xl text-base leading-none"
                        />
                      </div>
                      <div>
                        <Label htmlFor="timezone">Timezone</Label>
                        <Input
                          id="timezone"
                          name="profile-timezone"
                          type="text"
                          value={timezone}
                          onChange={(e) => {
                            setTimezone(e.target.value)
                            setBasicFieldErrors((current) => ({ ...current, timezone: undefined }))
                          }}
                          autoComplete="off"
                          inputMode="text"
                          className="rounded-xl text-base leading-none"
                          aria-invalid={Boolean(basicFieldErrors.timezone)}
                          aria-describedby={basicFieldErrors.timezone ? "timezone-error" : undefined}
                        />
                        {basicFieldErrors.timezone && (
                          <p id="timezone-error" className="mt-1 text-sm text-destructive" role="alert">
                            {basicFieldErrors.timezone}
                          </p>
                        )}
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="headline">Headline</Label>
                      <Input
                        id="headline"
                        name="profile-headline"
                        type="text"
                        value={headline}
                        onChange={(e) => setHeadline(e.target.value)}
                        autoComplete="off"
                        inputMode="text"
                        className="rounded-xl text-base leading-none"
                      />
                    </div>

                    <div>
                      <Label htmlFor="tags">Tags (comma separated)</Label>
                      <Input
                        id="tags"
                        name="profile-tags"
                        type="text"
                        value={tags.join(", ")}
                        onChange={(e) => {
                          setTags(e.target.value.split(",").map((t) => t.trim()))
                          setBasicFieldErrors((current) => ({ ...current, tags: undefined }))
                        }}
                        autoComplete="off"
                        inputMode="text"
                        className="rounded-xl text-base leading-none"
                        placeholder="Add up to 3 tags"
                        aria-invalid={Boolean(basicFieldErrors.tags)}
                        aria-describedby={basicFieldErrors.tags ? "tags-error" : undefined}
                      />
                      {basicFieldErrors.tags && (
                        <p id="tags-error" className="mt-1 text-sm text-destructive" role="alert">
                          {basicFieldErrors.tags}
                        </p>
                      )}
                    </div>

                    <div>
                      <Label htmlFor="bio">Bio</Label>
                      <Textarea
                        id="bio"
                        value={bio}
                        onChange={(e) => setBio(e.target.value)}
                        rows={4}
                        className="rounded-xl text-base"
                      />
                    </div>
                  </div>
                </div>
                <div className="flex justify-center">
                  <Button
                    type="button"
                    disabled={savingBasic}
                    onClick={handleSaveBasicInfo}
                    size="auto"
                  >
                    <Save className="mr-2 h-4 w-4" />
                    {savingBasic ? "Saving..." : "Save Changes"}
                  </Button>
                </div>
              </form>
            ) : (
              <div className="flex flex-col md:flex-row gap-6 items-start w-full">
                <div className="flex flex-col items-center md:items-start gap-4">
                  <Avatar className="h-24 w-24 border-4 border-white shadow-lg">
                    <AvatarImage src={avatarUrl ?? undefined} />
                    <AvatarFallback className="bg-mentor-accent text-white text-2xl">{avatarInitials}</AvatarFallback>
                  </Avatar>
                  <div className="text-center md:text-left">
                    <h2 className="text-xl font-medium text-gray-900 mb-1">{displayName}</h2>
                    <p className="text-sm text-gray-600 flex items-center gap-1 justify-center md:justify-start">
                      <MapPin className="h-3 w-3" />
                      {location || "Add location"} - {timezone}
                    </p>
                  </div>
                </div>

                <div className="flex-1">
                  <div className="flex flex-wrap gap-2 mb-4">
                    {tags.length === 0 && <span className="text-sm text-gray-500">Add up to 3 tags</span>}
                    {tags.map((tag, i) => (
                      <Badge key={i} variant="secondary" className="bg-mentor-accent/10 text-mentor-accent border-mentor-accent/20">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                  <p className="text-gray-700 leading-relaxed">
                    {bio || <span className="text-muted-foreground italic">No bio yet</span>}
                  </p>
                </div>
              </div>
            )}
          </CardContent>
          </div>
        </Card>

        {/* Fun facts + What I'm working on */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {/* Fun facts */}
          <Card maxWidth="none">
            <CardTitle title="Fun facts" subtitle="Getting to know me better" subtitleSize="big"/>
              {canEdit && (
                <Button
                  variant="ghost"
                  size="auto"
                  onClick={() => {
                    if (editingFunFacts) {
                      restoreFieldsFromProfile()
                      setEditingFunFacts(false)
                    } else {
                      setEditingFunFacts(true)
                    }
                  }}
                  aria-label={editingFunFacts ? "Cancel editing fun facts" : "Edit fun facts"}
                  className="absolute top-6 right-6 text-gray-600 hover:text-gray-900 rounded-xl"
                >
                  {editingFunFacts ? <X className="h-4 w-4" /> : <Pencil className="h-4 w-4" />}
                </Button>
              )}
            <CardContent className="space-y-3 items-start">
              {editingFunFacts ? (
                <form
                  className="space-y-3 w-full"
                  autoComplete="off"
                  onSubmit={(e) => e.preventDefault()}
                >
                  {funFacts.map((fact, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <Input
                        name={`fun-fact-${i}`}
                        type="text"
                        value={fact}
                        onChange={(e) => updateArrayItem(funFacts, i, e.target.value, setFunFacts)}
                        autoComplete="off"
                        inputMode="text"
                        className="rounded-xl text-base leading-none"
                        placeholder="Enter a fun fact..."
                      />
                      <Button
                        variant="ghost"
                        size="auto"
                        onClick={() => removeArrayItem(funFacts, i, setFunFacts)}
                        className="shrink-0 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-full px-2.5 py-1.5 h-8 w-8"
                        type="button"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                  <div className="flex justify-center">
                    {funFacts.length < 5 && (
                      <Button
                        variant="ghost"
                        size="auto"
                        onClick={() => addArrayItem(funFacts, setFunFacts, 5)}
                        className="rounded-xl"
                        type="button"
                      >
                        Add Fun Fact
                      </Button>
                    )}
                  </div>
                  <div className="flex justify-center pt-2">
                    <Button
                      disabled={savingFunFacts}
                      size="auto"
                      onClick={handleSaveFunFacts}
                      type="button"
                    >
                      <Save className="mr-2 h-4 w-4" />
                      {savingFunFacts ? "Saving..." : "Save"}
                    </Button>
                  </div>
                </form>
              ) : (
                <>
                  {funFacts.length === 0 ? (
                    <p className="text-sm text-muted-foreground italic">No fun facts yet</p>
                  ) : (
                    <ul className="space-y-2 list-disc list-inside">
                      {funFacts.map((fact, i) => (
                        <li key={i} className="text-sm text-gray-700 leading-relaxed pl-2">
                          {fact}
                        </li>
                      ))}
                    </ul>
                  )}
                </>
              )}
            </CardContent>
          </Card>

          {/* What I'm working on */}
          <Card maxWidth="none">
            <CardTitle title="What I'm working on"/>
              {canEdit && (
                <Button
                  variant="ghost"
                  size="auto"
                  onClick={() => {
                    if (editingWorkingOn) {
                      restoreFieldsFromProfile()
                      setEditingWorkingOn(false)
                    } else {
                      setEditingWorkingOn(true)
                    }
                  }}
                  aria-label={editingWorkingOn ? "Cancel editing what I am working on" : "Edit what I am working on"}
                  className="absolute top-6 right-6 text-gray-600 hover:text-gray-900 rounded-xl"
                >
                  {editingWorkingOn ? <X className="h-4 w-4" /> : <Pencil className="h-4 w-4" />}
                </Button>
            )}
            <CardContent className="items-start">
              {editingWorkingOn ? (
                <form
                  className="space-y-3 w-full"
                  autoComplete="off"
                  onSubmit={(e) => e.preventDefault()}
                >
                  {workingOn.map((item, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <Input
                        name={`working-on-${i}`}
                        type="text"
                        value={item}
                        onChange={(e) => updateArrayItem(workingOn, i, e.target.value, setWorkingOn)}
                        autoComplete="off"
                        inputMode="text"
                        className="rounded-xl text-base leading-none"
                        placeholder="What are you working on..."
                      />
                      <Button
                        variant="ghost"
                        size="auto"
                        onClick={() => removeArrayItem(workingOn, i, setWorkingOn)}
                        className="shrink-0 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-xl"
                        type="button"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                  <div className="flex justify-center">
                    {workingOn.length < 5 && (
                      <Button
                        variant="ghost"
                        size="auto"
                        onClick={() => addArrayItem(workingOn, setWorkingOn, 5)}
                        className="rounded-xl"
                        type="button"
                      >
                        Add Item
                      </Button>
                    )}
                  </div>
                  <div className="flex justify-center pt-2">
                    <Button
                      disabled={savingWorkingOn}
                      onClick={handleSaveWorkingOn}
                      type="button"
                      size="auto"
                    >
                      <Save className="mr-2 h-4 w-4" />
                      {savingWorkingOn ? "Saving..." : "Save"}
                    </Button>
                  </div>
                </form>
              ) : (
                <>
                  {workingOn.length === 0 ? (
                    <p className="text-sm text-muted-foreground italic">Nothing listed yet</p>
                  ) : (
                    <ul className="space-y-2 list-disc list-inside">
                      {workingOn.map((item, i) => (
                        <li key={i} className="text-sm text-gray-700 leading-relaxed pl-2">
                          {item}
                        </li>
                      ))}
                    </ul>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Things I don't love + boundaries */}
        <Card maxWidth="none" className="mb-6">
            <CardTitle title="Things I don't love (so we can work well together)"/>
            {canEdit && (
              <Button
                variant="ghost"
                size="auto"
                onClick={() => {
                  if (editingBoundaries) {
                    restoreFieldsFromProfile()
                    setEditingBoundaries(false)
                  } else {
                    setEditingBoundaries(true)
                  }
                }}
                aria-label={editingBoundaries ? "Cancel editing boundaries" : "Edit boundaries"}
                className="absolute top-6 right-6 text-gray-600 hover:text-gray-900 rounded-xl"
              >
                {editingBoundaries ? <X className="h-4 w-4" /> : <Pencil className="h-4 w-4" />}
              </Button>
            )}
            <CardContent className="items-start">
            {editingBoundaries ? (
              <form
                className="space-y-6 w-full"
                autoComplete="off"
                onSubmit={(e) => e.preventDefault()}
              >
                <div>
                  <h3 className="font-medium text-gray-900 mb-3">Not my favorite</h3>
                  <div className="space-y-3">
                    {dontLove.map((item, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <Input
                          name={`dont-love-${i}`}
                          type="text"
                          value={item}
                          onChange={(e) => updateArrayItem(dontLove, i, e.target.value, setDontLove)}
                          autoComplete="off"
                          inputMode="text"
                          className="rounded-xl text-base leading-none"
                          placeholder="What don't you love..."
                        />
                        <Button
                          variant="ghost"
                            size="auto"
                          onClick={() => removeArrayItem(dontLove, i, setDontLove)}
                          className="shrink-0 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-xl"
                          type="button"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                    <div className="flex justify-center">
                      {dontLove.length < 5 && (
                        <Button
                          variant="ghost"
                          size="auto"
                          onClick={() => addArrayItem(dontLove, setDontLove, 5)}
                          className="rounded-xl"
                          type="button"
                        >
                          Add Item
                        </Button>
                      )}
                    </div>
                  </div>
                </div>

                <div className="pt-4 border-t border-gray-400">
                  <h3 className="font-medium text-gray-900 mb-3">Clear boundaries</h3>
                  <div className="space-y-3">
                    {boundaries.map((item, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <Input
                          name={`boundary-${i}`}
                          type="text"
                          value={item}
                          onChange={(e) => updateArrayItem(boundaries, i, e.target.value, setBoundaries)}
                          autoComplete="off"
                          inputMode="text"
                          className="rounded-xl text-base leading-none"
                          placeholder="Set a boundary..."
                        />
                        <Button
                          variant="ghost"
                            size="auto"
                          onClick={() => removeArrayItem(boundaries, i, setBoundaries)}
                          type="button"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                    <div className="flex justify-center">
                      {boundaries.length < 5 && (
                        <Button
                          variant="ghost"
                          size="auto"
                          onClick={() => addArrayItem(boundaries, setBoundaries, 5)}
                          className="rounded-xl"
                          type="button"
                        >
                          Add Boundary
                        </Button>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex justify-center pt-2">
                  <Button
                    disabled={savingBoundaries}
                    onClick={handleSaveBoundaries}
                    size="auto"
                    type="button"
                  >
                    <Save className="mr-2 h-4 w-4" />
                    {savingBoundaries ? "Saving..." : "Save"}
                  </Button>
                </div>
              </form>
            ) : (
              <>
                <div>
                  <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                    Not my favorite
                  </h3>
                  {dontLove.length === 0 ? (
                    <p className="text-sm text-muted-foreground italic">Nothing listed yet</p>
                  ) : (
                    <ul className="space-y-2">
                      {dontLove.map((item, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-gray-700 leading-relaxed">
                          <span>•</span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <div className="pt-4 border-t border-gray-400">
                  <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                    Clear boundaries
                  </h3>
                  {boundaries.length === 0 ? (
                    <p className="text-sm text-muted-foreground italic">No boundaries set yet</p>
                  ) : (
                    <ul className="space-y-2">
                      {boundaries.map((item, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-gray-700 leading-relaxed">
                          <span>•</span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </>
            )}
          </CardContent>
        </Card>

        <Card maxWidth="none" className="mb-6">
            <CardTitle title="Contact & basics"/>
            {canEdit && (
              <Button
                variant="ghost"
                size="auto"
                onClick={() => {
                  if (editingContact) {
                    restoreFieldsFromProfile()
                    setEditingContact(false)
                  } else {
                    setEditingContact(true)
                  }
                }}
                aria-label={editingContact ? "Cancel editing contact information" : "Edit contact information"}
                className="absolute top-6 right-6 text-gray-600 hover:text-gray-900 rounded-xl"
              >
                {editingContact ? <X className="h-4 w-4" /> : <Pencil className="h-4 w-4" />}
              </Button>
            )}
            <CardContent className="space-y-4 items-start">
            {editingContact ? (
              <form
                className="space-y-4"
                autoComplete="off"
                onSubmit={(e) => e.preventDefault()}
              >
                <div>
                  <div className="flex items-center gap-2">
                    <Label htmlFor="email">Email</Label>
                    <TooltipProvider delayDuration={150}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            className="text-gray-500 hover:text-gray-700"
                            aria-label="How to change email"
                          >
                            <Info className="h-4 w-4" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="top" align="start">
                          Contact support to change your email.
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    readOnly
                    disabled
                    autoComplete="email"
                    className="rounded-xl text-base leading-none"
                  />
                </div>

                <div>
                  <Label htmlFor="phoneNumber">Phone</Label>
                  <Input
                    id="phoneNumber"
                    type="tel"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    autoComplete="tel"
                    className="rounded-xl text-base leading-none"
                    placeholder="(555) 555-5555"
                  />
                </div>

                <div>
                  <Label htmlFor="responseTime">Response Time</Label>
                  <Input
                    id="responseTime"
                    name="response-time"
                    type="text"
                    value={responseTime}
                    onChange={(e) => setResponseTime(e.target.value)}
                    autoComplete="off"
                    inputMode="text"
                    autoCapitalize="off"
                    autoCorrect="off"
                    className="rounded-xl text-base leading-none"
                  />
                </div>

                <div>
                  <Label htmlFor="contactMethod">Preferred Contact Method</Label>
                  <Input
                    id="contactMethod"
                    name="contact-method"
                    type="text"
                    value={contactMethod}
                    onChange={(e) => setContactMethod(e.target.value)}
                    autoComplete="off"
                    inputMode="text"
                    autoCapitalize="off"
                    autoCorrect="off"
                    className="rounded-xl text-base leading-none"
                  />
                </div>

                <div>
                  <Label htmlFor="languages">Languages (comma separated)</Label>
                  <Input
                    id="languages"
                    name="languages"
                    type="text"
                    value={languages.join(", ")}
                    onChange={(e) => setLanguages(e.target.value.split(",").map((l) => l.trim()))}
                    autoComplete="off"
                    inputMode="text"
                    autoCapitalize="off"
                    autoCorrect="off"
                    className="rounded-xl text-base leading-none"
                  />
                </div>

                <div className="flex justify-start pt-2">
                  <Button
                    type="button"
                    disabled={savingContact}
                    onClick={handleSaveContact}
                    size="auto"
                  >
                    <Save className="mr-2 h-4 w-4" />
                    {savingContact ? "Saving..." : "Save"}
                  </Button>
                </div>
              </form>
            ) : (
              <>
                <div className="flex items-start gap-3">
                  <Mail className="h-5 w-5 text-mentor-accent mt-0.5 shrink-0" />
                  <div>
                    <h4 className="font-medium text-gray-900 mb-1">Email</h4>
                    {email ? (
                      <a href={`mailto:${email}`} className="text-sm text-blue-600 underline-offset-2 hover:underline">
                        {email}
                      </a>
                    ) : (
                      <p className="text-sm text-gray-500">Add your email</p>
                    )}
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <MessageCircle className="h-5 w-5 text-mentor-accent mt-0.5 shrink-0" />
                  <div>
                    <h4 className="font-medium text-gray-900 mb-1">Phone</h4>
                    <p className="text-sm text-gray-700">{phoneNumber || "Add your mobile number to receive SMS reminders."}</p>
                    {profile?.sms_opted_out_at ? (
                      <p className="mt-1 text-xs text-amber-700">
                        SMS is currently opted out based on a carrier/provider signal.
                      </p>
                    ) : null}
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Clock className="h-5 w-5 text-mentor-accent mt-0.5 shrink-0" />
                  <div>
                    <h4 className="font-medium text-gray-900 mb-1">Response time</h4>
                    <p className="text-sm text-gray-700">{responseTime || "Share when people can expect a reply."}</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <MessageCircle className="h-5 w-5 text-mentor-accent mt-0.5 shrink-0" />
                  <div>
                    <h4 className="font-medium text-gray-900 mb-1">Preferred contact method</h4>
                    <p className="text-sm text-gray-700">{contactMethod || "Add how you prefer to be contacted."}</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Globe className="h-5 w-5 text-mentor-accent mt-0.5 shrink-0" />
                  <div>
                    <h4 className="font-medium text-gray-900 mb-1">Time zone</h4>
                    <p className="text-sm text-gray-700">{profile?.timezone ?? timezone}</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Globe className="h-5 w-5 text-mentor-accent mt-0.5 shrink-0" />
                  <div>
                    <h4 className="font-medium text-gray-900 mb-1">Languages</h4>
                    <p className="text-sm text-gray-700">{languages.length ? languages.join(", ") : "Add languages"}</p>
                  </div>
                </div>

                <div className="pt-4 border-t border-gray-200">
                  <p className="text-xs text-gray-600 leading-relaxed">
                    If you're in crisis or need immediate help, please contact a trusted adult, local emergency services,
                    or a crisis line in your area. This mentorship is not an emergency service.
                  </p>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {canEdit && <MobileNotificationSettings />}
        {canEdit && (
          <Card maxWidth="none" className="mb-6">
            <CardTitle title="Notifications" />
            <Button
              variant="ghost"
              size="auto"
              onClick={() => {
                if (editingNotifications) {
                  restoreFieldsFromProfile()
                  setEditingNotifications(false)
                } else {
                  setEditingNotifications(true)
                }
              }}
              aria-label={editingNotifications ? "Cancel editing notifications" : "Edit notifications"}
              className="absolute top-6 right-6 text-gray-600 hover:text-gray-900 rounded-xl"
            >
              {editingNotifications ? <X className="h-4 w-4" /> : <Pencil className="h-4 w-4" />}
            </Button>
            <CardContent className="space-y-4 items-start">
              {editingNotifications ? (
                <form className="space-y-4 w-full" autoComplete="off" onSubmit={(e) => e.preventDefault()}>
                  <div className="grid w-full grid-cols-1 items-start gap-x-6 gap-y-4 sm:grid-cols-[minmax(180px,max-content)_auto] sm:items-center">
                    <Label htmlFor="meeting-notification-enabled">Enable meeting notifications</Label>
                    <div className="sm:justify-self-end">
                      <Switch
                        id="meeting-notification-enabled"
                        checked={meetingNotificationsEnabled}
                        onCheckedChange={(checked) => setMeetingNotificationsEnabled(Boolean(checked))}
                      />
                    </div>

                    {meetingNotificationsEnabled && (
                      <>
                        <Label htmlFor="meeting-notification-in-app">In-app reminders</Label>
                        <div className="sm:justify-self-end">
                          <Switch
                            id="meeting-notification-in-app"
                            checked={inAppMeetingRemindersEnabled}
                            onCheckedChange={(checked) => setInAppMeetingRemindersEnabled(Boolean(checked))}
                          />
                        </div>

                        <Label htmlFor="mentorship-recap-notifications">For next time notes ready</Label>
                        <div className="sm:justify-self-end">
                          <Switch
                            id="mentorship-recap-notifications"
                            checked={mentorshipRecapNotificationsEnabled}
                            onCheckedChange={(checked) => setMentorshipRecapNotificationsEnabled(Boolean(checked))}
                          />
                        </div>

                        <Label htmlFor="meeting-notification-channel">Notification channel</Label>
                        <ToggleGroup
                          id="meeting-notification-channel"
                          type="single"
                          value={meetingNotificationChannel}
                          onValueChange={(value) => {
                            if (!value) return
                            setMeetingNotificationChannel(value as "email" | "sms" | "email_sms")
                          }}
                          className="flex flex-wrap justify-start gap-2 sm:justify-self-end"
                        >
                          <ToggleGroupItem value="email">Email</ToggleGroupItem>
                          <ToggleGroupItem value="sms">SMS</ToggleGroupItem>
                          <ToggleGroupItem value="email_sms">Email & SMS</ToggleGroupItem>
                        </ToggleGroup>

                        <Label htmlFor="meeting-reminder-timing">Reminder timing</Label>
                        <Select
                          value={String(meetingNotificationLeadMinutes)}
                          onValueChange={(value) => setMeetingNotificationLeadMinutes(Number(value))}
                        >
                          <SelectTrigger id="meeting-reminder-timing" className="w-full rounded-xl sm:w-[220px] sm:justify-self-end">
                            <SelectValue placeholder="Choose reminder timing" />
                          </SelectTrigger>
                          <SelectContent>
                            {(profile?.settings?.meeting_notification_fixed_options?.length
                              ? profile.settings.meeting_notification_fixed_options
                              : DEFAULT_FIXED_LEAD_OPTIONS
                            ).map((option) => (
                              <SelectItem key={String(option)} value={String(option)}>
                                {option} minutes before
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </>
                    )}
                  </div>

                  <div className="flex justify-start pt-2">
                    <Button type="button" disabled={savingNotifications} onClick={handleSaveNotifications} size="auto">
                      <Save className="mr-2 h-4 w-4" />
                      {savingNotifications ? "Saving..." : "Save"}
                    </Button>
                  </div>
                </form>
              ) : (
                <>
                  <div className="flex items-start gap-3">
                    <Clock className="h-5 w-5 text-mentor-accent mt-0.5 shrink-0" />
                    <div>
                      <h4 className="font-medium text-gray-900 mb-1">Meeting reminders</h4>
                      <p className="text-sm text-gray-700">
                        {meetingNotificationsEnabled ? "Enabled" : "Disabled"}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <MessageCircle className="h-5 w-5 text-mentor-accent mt-0.5 shrink-0" />
                    <div>
                      <h4 className="font-medium text-gray-900 mb-1">In-app reminders</h4>
                      <p className="text-sm text-gray-700">
                        {inAppMeetingRemindersEnabled ? "Enabled while app is open" : "Disabled"}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <MessageCircle className="h-5 w-5 text-mentor-accent mt-0.5 shrink-0" />
                    <div>
                      <h4 className="font-medium text-gray-900 mb-1">For next time notes ready</h4>
                      <p className="text-sm text-gray-700">
                        {mentorshipRecapNotificationsEnabled ? "Sent once per session through your channel above" : "Disabled"}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <Mail className="h-5 w-5 text-mentor-accent mt-0.5 shrink-0" />
                    <div>
                      <h4 className="font-medium text-gray-900 mb-1">Channel</h4>
                      <p className="text-sm text-gray-700">
                        {meetingNotificationChannel === "email"
                          ? "Email"
                          : meetingNotificationChannel === "sms"
                            ? "SMS"
                            : "Email & SMS"}
                      </p>
                      {profile?.sms_opted_out_at ? (
                        <p className="mt-1 text-xs text-amber-700">
                          Outbound SMS is paused until you opt in again by text.
                        </p>
                      ) : null}
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <Globe className="h-5 w-5 text-mentor-accent mt-0.5 shrink-0" />
                    <div>
                      <h4 className="font-medium text-gray-900 mb-1">Reminder timing</h4>
                      <p className="text-sm text-gray-700">
                        {meetingNotificationLeadMinutes} minutes before
                      </p>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        )}

        {canEdit && (
          <Card maxWidth="none">
            <CardContent className="items-start">
                <CardTitle className="text-2xl">
                Change Password
              </CardTitle>
              <p className="type-body-m text-foundation-base-darkbase-dark-300">
                Updating your password signs you out on all devices. You will need to sign in again.
              </p>

                <form
                  className="flex flex-col gap-6"
                  autoComplete="off"
                  onSubmit={(e) => e.preventDefault()}
                >
                  {requiresCurrentPassword && (
                    <PasswordInput
                      id="current-password"
                      name="current-password"
                      autoComplete="current-password"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      placeholder="Current Password *"
                      containerClassName="relative w-full sm:w-[400px]"
                      className="h-auto w-full bg-white/80 border-foundation-base-darkbase-dark-100 type-body-m text-foundation-base-darkbase-dark-500 placeholder:text-foundation-base-darkbase-dark-200 pr-16 sm:w-[400px]"
                      hideLabel="Hide"
                      showLabel="Show"
                    />
                  )}

                  <PasswordInput
                    name="new-password"
                    autoComplete="new-password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="New Password *"
                    containerClassName="relative w-full sm:w-[400px]"
                    className="h-auto w-full bg-white/80 border-foundation-base-darkbase-dark-100 type-body-m text-foundation-base-darkbase-dark-500 placeholder:text-foundation-base-darkbase-dark-200 pr-16 sm:w-[400px]"
                    hideLabel="Hide"
                    showLabel="Show"
                  />
                  {passwordValidationMessage && (
                    <p className="text-xs font-medium text-destructive">
                      {passwordValidationMessage}
                    </p>
                  )}

                  <PasswordInput
                    name="confirm-new-password"
                    autoComplete="new-password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm New Password *"
                    containerClassName="relative w-full sm:w-[400px]"
                    className="h-auto w-full bg-white/80 border-foundation-base-darkbase-dark-100 type-body-m text-foundation-base-darkbase-dark-500 placeholder:text-foundation-base-darkbase-dark-200 pr-16 sm:w-[400px]"
                    hideLabel="Hide"
                    showLabel="Show"
                  />

                  <Button
                    size="auto"
                    className="self-start"
                    type="button"
                    onClick={handleChangePassword}
                    disabled={
                      changingPassword ||
                      !newPassword ||
                      !confirmPassword ||
                      newPassword !== confirmPassword ||
                      Boolean(passwordValidationMessage) ||
                      (requiresCurrentPassword && !currentPassword)
                    }
                  >
                    {changingPassword ? "Updating..." : "Update Password"}
                  </Button>
                </form>
              </CardContent>
            </Card>
        )}
      </div>
    </div>
  )
}
